import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Space, Tooltip, Typography } from "antd";
import { useSelector } from "react-redux";

import { request } from "@/request";
import { selectMenu } from "@/redux/crud/selectors";

import {
  applyOptimisticAttendanceAction,
  computeAttendanceDurations,
  computeAttendanceStatus,
  formatDuration,
  getInitialAttendanceState,
} from "./hrmAttendanceUtils";

const { Text } = Typography;

const DEFAULT_SYNC_INTERVAL_MS = 60000;
const MIN_SYNC_INTERVAL_MS = 15000;

export default function HrmAttendanceWidget() {
  const { result: objMenu } = useSelector(selectMenu);
  const canViewAttendance = Boolean(objMenu?.HrmAttendance?.isView);

  const [attendanceState, setAttendanceState] = useState(getInitialAttendanceState);
  const [isSyncLoading, setSyncLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [timNow, setTimNow] = useState(new Date());

  const isSyncingRef = useRef(false);

  const syncIntervalMs = useMemo(() => {
    const parsedInterval = Number(process.env.REACT_APP_HRM_ATTENDANCE_SYNC_MS);
    if (!Number.isFinite(parsedInterval) || parsedInterval < MIN_SYNC_INTERVAL_MS) {
      return DEFAULT_SYNC_INTERVAL_MS;
    }
    return parsedInterval;
  }, []);

  const applyServerState = useCallback((response = {}) => {
    const objData = response?.objData || {};
    const objStatus = response?.objStatus || computeAttendanceStatus(objData);
    setAttendanceState({
      objData,
      objStatus,
    });
    setLastSyncedAt(new Date());
  }, []);

  const fetchTodayAttendanceState = useCallback(async () => {
    const historyResponse = await request.list({
      listURL: "get_attendance_history",
      options: {
        page: 1,
        pageSize: 1,
        strDate: new Date().toISOString(),
      },
    });

    if (!historyResponse?.success) {
      setAttendanceState(getInitialAttendanceState());
      return;
    }

    const latestDoc = historyResponse?.arrList?.[0] || {};
    const objData = latestDoc?.timCheckIn ? latestDoc : {};
    applyServerState({
      objData,
      objStatus: computeAttendanceStatus(objData),
    });
  }, [applyServerState]);

  const syncAttendanceState = useCallback(
    async ({ showLoading = false } = {}) => {
      if (!canViewAttendance || isSyncingRef.current) return;

      isSyncingRef.current = true;
      if (showLoading) {
        setSyncLoading(true);
      }

      try {
        await fetchTodayAttendanceState();
      } catch (error) {
        console.error("HRM attendance sync failed", error);
      } finally {
        if (showLoading) {
          setSyncLoading(false);
        }
        isSyncingRef.current = false;
      }
    },
    [canViewAttendance, fetchTodayAttendanceState]
  );

  useEffect(() => {
    if (!canViewAttendance) return undefined;

    syncAttendanceState({ showLoading: true });
    return undefined;
  }, [canViewAttendance, syncAttendanceState]);

  useEffect(() => {
    if (!canViewAttendance) return undefined;

    const syncInterval = setInterval(() => {
      if (document.hidden) return;
      syncAttendanceState();
    }, syncIntervalMs);

    return () => {
      clearInterval(syncInterval);
    };
  }, [canViewAttendance, syncAttendanceState, syncIntervalMs]);

  useEffect(() => {
    if (!canViewAttendance) return undefined;

    const timerInterval = setInterval(() => {
      if (document.hidden) return;
      setTimNow(new Date());
    }, 1000);

    return () => {
      clearInterval(timerInterval);
    };
  }, [canViewAttendance]);

  const handleAttendanceAction = async (strAction) => {
    if (!canViewAttendance || actionLoading) return;

    const optimisticData = applyOptimisticAttendanceAction(
      attendanceState.objData,
      strAction,
      new Date()
    );

    setAttendanceState({
      objData: optimisticData,
      objStatus: computeAttendanceStatus(optimisticData),
    });
    setActionLoading(strAction);

    try {
      const actionResponse = await request.post({
        entity: "check_in_out",
        jsonData: {
          strAction,
        },
      });

      if (actionResponse?.success && (actionResponse?.objData || actionResponse?.objStatus)) {
        applyServerState(actionResponse);
      } else {
        await syncAttendanceState();
      }
    } catch (error) {
      console.error("HRM attendance action failed", error);
      await syncAttendanceState();
    } finally {
      setActionLoading("");
    }
  };

  if (!canViewAttendance) {
    return null;
  }

  const { workDurationMs, breakDurationMs } = computeAttendanceDurations(
    attendanceState.objData,
    timNow
  );
  const canBreakAction =
    attendanceState.objStatus?.canBreakIn || attendanceState.objStatus?.canBreakOut;
  const isBreakOutAction = Boolean(attendanceState.objStatus?.canBreakOut);
  const breakAction = isBreakOutAction ? "BREAK_OUT" : "BREAK_IN";

  const tooltipTitle = (
    <div>
      <div>Work: {formatDuration(workDurationMs)}</div>
      <div>Break: {formatDuration(breakDurationMs)}</div>
      {lastSyncedAt ? (
        <div style={{ marginTop: 4, opacity: 0.75 }}>
          Synced: {lastSyncedAt.toLocaleTimeString()}
        </div>
      ) : null}
    </div>
  );

  return (
    <Tooltip title={tooltipTitle} placement="bottomRight">
      <div
        style={{
          float: "right",
          marginTop: 12,
          marginLeft: 10,
          padding: "6px 8px",
          borderRadius: 8,
          border: "1px solid #e8e8e8",
          backgroundColor: "#fff",
        }}
      >
        <Space size={6} align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>
            {attendanceState.objStatus?.strCurrentState || "NOT_CHECKED_IN"}
          </Text>
          <Button
            size="small"
            type="primary"
            onClick={() => handleAttendanceAction("CHECK_IN")}
            disabled={!attendanceState.objStatus?.canCheckIn || isSyncLoading}
            loading={actionLoading === "CHECK_IN"}
          >
            Check in
          </Button>
          <Button
            size="small"
            onClick={() => handleAttendanceAction(breakAction)}
            disabled={!canBreakAction || isSyncLoading}
            loading={actionLoading === breakAction}
          >
            {isBreakOutAction ? "Break out" : "Break in"}
          </Button>
          <Button
            size="small"
            danger
            onClick={() => handleAttendanceAction("CHECK_OUT")}
            disabled={!attendanceState.objStatus?.canCheckOut || isSyncLoading}
            loading={actionLoading === "CHECK_OUT"}
          >
            Check out
          </Button>
        </Space>
      </div>
    </Tooltip>
  );
}
